import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { checkAuthEligibility, registerOpenSignup, type AuthIntent } from '../api/auth'
import { setIdTokenGetter } from '../api/authToken'
import { authClient, toOperatorUser, type OperatorUser } from '../lib/auth-client'
import { logError } from '../lib/logError'

const EMAIL_LINK_KEY = 'bq_tasks_emailForSignIn'
const DISPLAY_NAME_KEY = 'bq_tasks_displayNameForSignUp'

type AuthContextValue = {
  user: OperatorUser | null
  loading: boolean
  error: string | null
  clearError: () => void
  sendMagicLink: (email: string, opts?: { displayName?: string; intent?: AuthIntent }) => Promise<void>
  completeOpenSignup: () => Promise<void>
  signOutUser: () => Promise<void>
  getIdToken: () => Promise<string | null>
  /** True when magic-link verify redirected with pending TOTP. */
  totpRequired: boolean
  confirmTotpChallenge: (code: string) => Promise<void>
  clearTotpChallenge: () => void
  signInWithPasskey: () => Promise<void>
  updateDisplayName: (name: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function ensureEligible(email: string, intent: AuthIntent): Promise<void> {
  const result = await checkAuthEligibility(email, intent)
  if (result.eligible) return
  if (result.reason === 'invalid_email') {
    throw new Error('Enter a valid email address.')
  }
  if (result.reason === 'signup_pending') {
    throw new Error(
      'Thanks — we recorded your signup request. An operator will approve access shortly.',
    )
  }
  throw new Error(
    'This workspace is invite-only. Ask an operator to add your email to the allowlist.',
  )
}

function stripAuthQueryParams() {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('twoFactorRedirect') && !url.search) return
  url.searchParams.delete('twoFactorRedirect')
  const qs = url.searchParams.toString()
  window.history.replaceState({}, document.title, url.pathname + (qs ? `?${qs}` : '') + url.hash)
}

async function applyPendingDisplayName() {
  const name = window.localStorage.getItem(DISPLAY_NAME_KEY)?.trim()
  if (!name) return
  try {
    await authClient.updateUser({ name })
  } catch (err) {
    logError('auth.displayName', err)
  } finally {
    window.localStorage.removeItem(DISPLAY_NAME_KEY)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<OperatorUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totpRequired, setTotpRequired] = useState(false)

  const refreshSession = useCallback(async () => {
    const { data } = await authClient.getSession()
    if (data?.user) {
      setUser(toOperatorUser(data.user))
      return toOperatorUser(data.user)
    }
    setUser(null)
    return null
  }, [])

  const getIdToken = useCallback(async () => {
    const { data: session } = await authClient.getSession()
    if (!session?.user?.emailVerified) return null
    const { data, error: tokenErr } = await authClient.token()
    if (tokenErr || !data?.token) return null
    return data.token
  }, [])

  useEffect(() => {
    setIdTokenGetter(getIdToken)
    return () => setIdTokenGetter(null)
  }, [getIdToken])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const params = new URLSearchParams(window.location.search)
        if (params.get('twoFactorRedirect') === '1') {
          setTotpRequired(true)
          stripAuthQueryParams()
        }
        await refreshSession()
        await applyPendingDisplayName()
        await refreshSession()
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Couldn't restore session")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refreshSession])

  const sendMagicLink = useCallback(
    async (email: string, opts?: { displayName?: string; intent?: AuthIntent }) => {
      setError(null)
      const trimmed = email.trim()
      if (!trimmed) {
        setError('Enter your email')
        return
      }
      const intent = opts?.intent ?? 'signin'
      try {
        await ensureEligible(trimmed, intent)
        const { error: sendErr } = await authClient.signIn.magicLink({
          email: trimmed,
          callbackURL: `${window.location.origin}/`,
          name: opts?.displayName?.trim() || undefined,
        })
        if (sendErr) throw new Error(sendErr.message || "Couldn't send the email link")
        window.localStorage.setItem(EMAIL_LINK_KEY, trimmed)
        const name = opts?.displayName?.trim()
        if (name) window.localStorage.setItem(DISPLAY_NAME_KEY, name)
        else window.localStorage.removeItem(DISPLAY_NAME_KEY)
      } catch (err) {
        logError('auth.magicLink', err)
        setError(err instanceof Error ? err.message : "Couldn't send the email link")
      }
    },
    [],
  )

  const completeOpenSignup = useCallback(async () => {
    const { data } = await authClient.getSession()
    const email = data?.user?.email?.trim()
    if (!data?.user || !email || !data.user.emailVerified) return
    try {
      const eligibility = await checkAuthEligibility(email, 'signup')
      if (!eligibility.open_signup) return
      await registerOpenSignup()
    } catch (err) {
      logError('auth.register', err)
    }
  }, [])

  const signOutUser = useCallback(async () => {
    setError(null)
    setTotpRequired(false)
    await authClient.signOut()
    setUser(null)
  }, [])

  const confirmTotpChallenge = useCallback(
    async (code: string) => {
      setError(null)
      const { error: verifyErr } = await authClient.twoFactor.verifyTotp({ code })
      if (verifyErr) {
        throw new Error(verifyErr.message || 'Invalid authenticator code')
      }
      setTotpRequired(false)
      await applyPendingDisplayName()
      await refreshSession()
    },
    [refreshSession],
  )

  const clearTotpChallenge = useCallback(() => setTotpRequired(false), [])

  const signInWithPasskey = useCallback(async () => {
    setError(null)
    try {
      const { error: pkErr } = await authClient.signIn.passkey({ autoFill: true })
      if (pkErr) throw new Error(pkErr.message || "Couldn't sign in with passkey")
      await refreshSession()
    } catch (err) {
      logError('auth.passkey', err)
      setError(err instanceof Error ? err.message : "Couldn't sign in with passkey")
    }
  }, [refreshSession])

  const updateDisplayName = useCallback(
    async (name: string) => {
      const { error: updErr } = await authClient.updateUser({ name: name.trim() || ' ' })
      if (updErr) throw new Error(updErr.message || "Couldn't update name")
      await refreshSession()
    },
    [refreshSession],
  )

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      clearError: () => setError(null),
      sendMagicLink,
      completeOpenSignup,
      signOutUser,
      getIdToken,
      totpRequired,
      confirmTotpChallenge,
      clearTotpChallenge,
      signInWithPasskey,
      updateDisplayName,
    }),
    [
      user,
      loading,
      error,
      sendMagicLink,
      completeOpenSignup,
      signOutUser,
      getIdToken,
      totpRequired,
      confirmTotpChallenge,
      clearTotpChallenge,
      signInWithPasskey,
      updateDisplayName,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth requires AuthProvider')
  return ctx
}
